// src/constants/index.ts
var PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100
};
var USER_ROLES = {
  CUSTOMER: "CUSTOMER",
  CASHIER: "CASHIER",
  // POS terminal only
  STAFF: "STAFF",
  ADMIN: "ADMIN",
  SUPER_ADMIN: "SUPER_ADMIN"
};
var ADMIN_ROLES = [USER_ROLES.STAFF, USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN];
var POS_ROLES = [
  USER_ROLES.CASHIER,
  USER_ROLES.STAFF,
  USER_ROLES.ADMIN,
  USER_ROLES.SUPER_ADMIN
];
var STAFF_ROLES = [
  USER_ROLES.CASHIER,
  USER_ROLES.STAFF,
  USER_ROLES.ADMIN,
  USER_ROLES.SUPER_ADMIN
];
var ORDER_STATUS = {
  PENDING: "PENDING",
  CONFIRMED: "CONFIRMED",
  PROCESSING: "PROCESSING",
  SHIPPED: "SHIPPED",
  DELIVERED: "DELIVERED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  REFUNDED: "REFUNDED",
  PARTIALLY_REFUNDED: "PARTIALLY_REFUNDED"
};
var TERMINAL_ORDER_STATUSES = [
  ORDER_STATUS.DELIVERED,
  ORDER_STATUS.COMPLETED,
  ORDER_STATUS.CANCELLED,
  ORDER_STATUS.REFUNDED
];
var ORDER_CHANNEL = {
  ONLINE: "ONLINE",
  POS: "POS"
};
var PAYMENT_METHOD = {
  CASH: "CASH",
  CARD: "CARD",
  MOBILE_BANKING: "MOBILE_BANKING",
  QR_CODE: "QR_CODE",
  CREDIT: "CREDIT",
  GIFT_CARD: "GIFT_CARD",
  SPLIT: "SPLIT"
};
var PAYMENT_STATUS = {
  PENDING: "PENDING",
  AUTHORIZED: "AUTHORIZED",
  CAPTURED: "CAPTURED",
  FAILED: "FAILED",
  VOIDED: "VOIDED",
  REFUNDED: "REFUNDED",
  PARTIALLY_REFUNDED: "PARTIALLY_REFUNDED"
};
var PRODUCT_STATUS = {
  ACTIVE: "ACTIVE",
  DRAFT: "DRAFT",
  ARCHIVED: "ARCHIVED"
};
var HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503
};
var ERROR_CODES = {
  // Auth
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  TOKEN_INVALID: "TOKEN_INVALID",
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  // Validation
  VALIDATION_ERROR: "VALIDATION_ERROR",
  DUPLICATE_ENTRY: "DUPLICATE_ENTRY",
  // Resources
  NOT_FOUND: "NOT_FOUND",
  INSUFFICIENT_STOCK: "INSUFFICIENT_STOCK",
  // Server
  INTERNAL_ERROR: "INTERNAL_ERROR",
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
  // Rate limiting
  RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED"
};
var CACHE_TTL = {
  SHORT: 60,
  // 1 minute — product stock levels
  MEDIUM: 5 * 60,
  // 5 minutes — product listings
  LONG: 30 * 60,
  // 30 minutes — category trees
  VERY_LONG: 60 * 60
  // 1 hour — static config
};
var UPLOAD = {
  MAX_IMAGE_SIZE_BYTES: 5 * 1024 * 1024,
  // 5 MB
  ALLOWED_IMAGE_TYPES: ["image/jpeg", "image/png", "image/webp", "image/gif"],
  MAX_IMAGES_PER_PRODUCT: 10
};
var CURRENCY = {
  CODE: "MMK",
  // Myanmar Kyat
  SYMBOL: "K",
  DECIMAL_PLACES: 0
  // Kyat has no minor unit in practice
};
var WS_EVENTS = {
  // POS
  POS_SALE_COMPLETED: "pos:sale_completed",
  POS_PING: "pos:ping",
  POS_SESSION_EVENT: "pos:session_event",
  // Inventory
  INVENTORY_UPDATED: "inventory:updated",
  INVENTORY_LOW_STOCK: "inventory:low_stock",
  // Orders
  ORDER_STATUS_CHANGED: "order:status_changed",
  ORDER_CREATED: "order:created",
  // Connection
  CONNECTED: "connected",
  DISCONNECTED: "disconnected"
};

// src/utils/index.ts
function formatCurrency(amount, currencyCode = CURRENCY.CODE, symbol = CURRENCY.SYMBOL) {
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: CURRENCY.DECIMAL_PLACES,
    maximumFractionDigits: CURRENCY.DECIMAL_PLACES
  }).format(amount);
  return currencyCode === "MMK" ? `${symbol} ${formatted}` : `${symbol}${formatted}`;
}
function toStorageAmount(displayAmount) {
  return Math.round(displayAmount * Math.pow(10, CURRENCY.DECIMAL_PLACES));
}
function buildPaginationMeta(total, query) {
  const totalPages = Math.ceil(total / query.limit);
  return {
    page: query.page,
    limit: query.limit,
    total,
    totalPages,
    hasNextPage: query.page < totalPages,
    hasPrevPage: query.page > 1
  };
}
function normalizePagination(query) {
  const page = Math.max(1, query.page ?? PAGINATION.DEFAULT_PAGE);
  const limit = Math.min(
    PAGINATION.MAX_LIMIT,
    Math.max(1, query.limit ?? PAGINATION.DEFAULT_LIMIT)
  );
  return { page, limit };
}
function paginationToSkipTake(page, limit) {
  return { skip: (page - 1) * limit, take: limit };
}
function slugify(text) {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
}
function generateOrderNumber(sequence) {
  const year = (/* @__PURE__ */ new Date()).getFullYear();
  const padded = String(sequence).padStart(5, "0");
  return `ORD-${year}-${padded}`;
}
function truncate(str, maxLength) {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + "...";
}
function formatDate(date, locale = "en-US") {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric"
  }).format(typeof date === "string" ? new Date(date) : date);
}
function formatDateTime(date, locale = "en-US") {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(typeof date === "string" ? new Date(date) : date);
}
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
function isValidPhone(phone) {
  return /^(\+?95|0)9\d{7,9}$/.test(phone.replace(/\s/g, ""));
}
function pick(obj, keys) {
  return keys.reduce(
    (acc, key) => {
      if (key in obj) acc[key] = obj[key];
      return acc;
    },
    {}
  );
}
function omit(obj, keys) {
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
}
function compact(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== null && v !== void 0)
  );
}
function applyDiscount(price, discountPercent) {
  return Math.round(price * (1 - discountPercent / 100));
}
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
function refundAmountFor(order, line, alreadyRefundedQty, qty) {
  const subtotal = order.lineItems.reduce((s, li) => s + li.unitPrice * li.quantity, 0);
  if (subtotal <= 0) return 0;
  const itemDiscount = order.discount?.type === "FREE_SHIPPING" ? 0 : order.discountAmount;
  const lineShare = Math.round(
    line.unitPrice * line.quantity * (subtotal - itemDiscount + order.taxAmount) / subtotal
  );
  const upTo = (n) => Math.round(lineShare * n / line.quantity);
  return upTo(alreadyRefundedQty + qty) - upTo(alreadyRefundedQty);
}

// src/validations/index.ts
import { z } from "zod";
var idSchema = z.string().uuid({ message: "Invalid ID format" });
var paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  sortBy: z.string().optional(),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var passwordSchema = z.string().min(8, "Password must be at least 8 characters").max(128, "Password too long").regex(/[A-Z]/, "Must contain an uppercase letter").regex(/[0-9]/, "Must contain a number");
var registerSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100).trim(),
  lastName: z.string().min(1, "Last name is required").max(100).trim(),
  email: z.string().email("Invalid email address").toLowerCase().trim(),
  password: passwordSchema,
  phone: z.string().regex(/^(\+?95|0)9\d{7,9}$/, "Invalid Myanmar phone number").optional()
});
var inviteStaffSchema = z.object({
  firstName: z.string().min(1).max(100).trim(),
  lastName: z.string().min(1).max(100).trim(),
  email: z.string().email().toLowerCase().trim(),
  role: z.enum(["CASHIER", "STAFF", "ADMIN"]),
  phone: z.string().regex(/^(\+?95|0)9\d{7,9}$/).optional(),
  locationIds: z.array(z.string()).optional(),
  storeId: z.string().uuid().optional()
});
var loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required")
});
var refreshTokenSchema = z.object({
  refreshToken: z.string().min(1, "Refresh token is required")
});
var updateProfileSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100).trim(),
  lastName: z.string().min(1, "Last name is required").max(100).trim(),
  phone: z.string().regex(/^(\+?95|0)9\d{7,9}$/, "Invalid Myanmar phone number").optional().nullable()
});
var changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8).regex(/[A-Z]/).regex(/[0-9]/),
  confirmPassword: z.string()
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address").toLowerCase().trim()
});
var resetPasswordSchema = z.object({
  token: z.string().min(1, "Reset token is required"),
  newPassword: passwordSchema,
  confirmPassword: z.string()
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var productVariantSchema = z.object({
  title: z.string().min(1, "Variant title is required").max(200),
  sku: z.string().min(1, "SKU is required").max(100),
  barcode: z.string().max(100).optional().nullable(),
  option1: z.string().max(200).optional().nullable(),
  option2: z.string().max(200).optional().nullable(),
  option3: z.string().max(200).optional().nullable(),
  price: z.number().int().nonnegative("Price must be a non-negative integer"),
  compareAtPrice: z.number().int().nonnegative().optional().nullable(),
  costPrice: z.number().int().nonnegative().optional().nullable(),
  weight: z.number().nonnegative().optional().nullable(),
  requiresShipping: z.boolean().default(true),
  isDefault: z.boolean().default(false),
  position: z.number().int().nonnegative().default(0),
  /** Initial stock quantity — used to seed InventoryLevel on creation, not stored on variant */
  stock: z.number().int().min(0).default(0)
});
var createProductSchema = z.object({
  name: z.string().min(1, "Name is required").max(500),
  shortDescription: z.string().max(500).optional().nullable(),
  description: z.string().max(2e5).optional().nullable(),
  status: z.enum(["ACTIVE", "DRAFT", "ARCHIVED"]).default("DRAFT"),
  tags: z.array(z.string().max(50)).max(20).default([]),
  vendor: z.string().max(200).optional().nullable(),
  taxable: z.boolean().default(true),
  /** Option axis labels — up to 3 (e.g. ["Size", "Color"]) */
  optionNames: z.array(z.string().max(100)).max(3).default([]),
  categoryIds: z.array(idSchema).default([]),
  variants: z.array(productVariantSchema).min(1, "At least one variant is required"),
  metaTitle: z.string().max(200).optional().nullable(),
  metaDescription: z.string().max(500).optional().nullable(),
  /** Target inventory location for initial stock — falls back to store default if omitted */
  locationId: idSchema.optional().nullable()
});
var updateProductSchema = createProductSchema.partial();
var productQuerySchema = paginationSchema.extend({
  status: z.enum(["ACTIVE", "DRAFT", "ARCHIVED", "all"]).optional(),
  categoryId: idSchema.optional(),
  search: z.string().max(200).optional(),
  minPrice: z.coerce.number().int().nonnegative().optional(),
  maxPrice: z.coerce.number().int().nonnegative().optional(),
  inStock: z.coerce.boolean().optional(),
  locationId: idSchema.optional(),
  include: z.string().optional()
});
var imageReorderSchema = z.object({
  images: z.array(z.object({ id: idSchema, position: z.number().int().nonnegative() }))
});
var orderAddressSchema = z.object({
  name: z.string().min(1).max(200),
  phone: z.string().regex(/^(\+?95|0)9\d{7,9}$/),
  line1: z.string().min(1).max(500),
  line2: z.string().max(500).optional().nullable(),
  city: z.string().min(1).max(200),
  state: z.string().max(200).optional().nullable(),
  postalCode: z.string().max(20).optional().nullable(),
  country: z.string().length(2).default("MM")
});
var orderLineItemSchema = z.object({
  variantId: idSchema,
  quantity: z.number().int().min(1, "Quantity must be at least 1")
});
var createOrderSchema = z.object({
  channel: z.enum(["ONLINE", "POS"]),
  lineItems: z.array(orderLineItemSchema).min(1, "Order must have at least one item"),
  payments: z.array(
    z.object({
      method: z.enum(["CASH", "CARD", "MOBILE_BANKING", "QR_CODE", "CREDIT", "GIFT_CARD"]),
      amount: z.number().int().positive("Payment amount must be positive")
    })
  ).min(1, "At least one payment is required"),
  customerId: idSchema.optional().nullable(),
  guestName: z.string().max(200).optional().nullable(),
  guestEmail: z.string().email().optional().nullable(),
  guestPhone: z.string().optional().nullable(),
  posSessionId: idSchema.optional().nullable(),
  discountCode: z.string().optional().nullable(),
  shippingAddress: orderAddressSchema.optional().nullable(),
  billingAddress: orderAddressSchema.optional().nullable(),
  locationId: idSchema.optional(),
  // inventory deduction target; defaults to store default location
  notes: z.string().max(2e3).optional().nullable()
});
var updateOrderStatusSchema = z.object({
  status: z.enum([
    "PENDING",
    "CONFIRMED",
    "PROCESSING",
    "SHIPPED",
    "DELIVERED",
    "COMPLETED",
    "CANCELLED"
  ]),
  note: z.string().max(500).optional()
});
var orderQuerySchema = paginationSchema.extend({
  status: z.enum([
    "PENDING",
    "CONFIRMED",
    "PROCESSING",
    "SHIPPED",
    "DELIVERED",
    "COMPLETED",
    "CANCELLED",
    "REFUNDED",
    "PARTIALLY_REFUNDED"
  ]).optional(),
  channel: z.enum(["ONLINE", "POS"]).optional(),
  paymentStatus: z.enum([
    "PENDING",
    "AUTHORIZED",
    "CAPTURED",
    "FAILED",
    "VOIDED",
    "REFUNDED",
    "PARTIALLY_REFUNDED"
  ]).optional(),
  fulfillmentStatus: z.enum(["UNFULFILLED", "PARTIALLY_FULFILLED", "FULFILLED", "RESTOCKED"]).optional(),
  statuses: z.string().optional(),
  // comma-separated list of status values
  customerId: idSchema.optional(),
  search: z.string().max(200).optional(),
  dateFrom: z.string().datetime({ offset: true }).optional(),
  dateTo: z.string().datetime({ offset: true }).optional(),
  sortBy: z.enum(["createdAt", "totalAmount", "orderNumber"]).optional()
});
var createRefundSchema = z.object({
  lineItems: z.array(
    z.object({
      orderLineItemId: idSchema,
      quantity: z.number().int().min(1),
      restockType: z.enum(["RESTOCK", "NO_RESTOCK"]).default("RESTOCK"),
      locationId: idSchema.optional().nullable()
    })
  ).min(1, "At least one line item is required"),
  reason: z.string().max(500).optional().nullable(),
  notes: z.string().max(1e3).optional().nullable()
});
var createCustomerSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100).trim(),
  lastName: z.string().min(1, "Last name is required").max(100).trim(),
  email: z.string().email("Invalid email address").toLowerCase().trim().optional().nullable(),
  phone: z.string().regex(/^(\+?95|0)9\d{7,9}$/, "Invalid Myanmar phone number").optional().nullable(),
  tags: z.array(z.string().max(50)).max(10).default([]),
  notes: z.string().max(2e3).optional().nullable(),
  acceptsMarketing: z.boolean().default(false)
});
var quickAddCustomerSchema = z.object({
  firstName: z.string().min(1).max(100).trim(),
  lastName: z.string().max(100).trim().default(""),
  email: z.string().email().toLowerCase().trim().optional().nullable(),
  phone: z.string().regex(/^(\+?95|0)9\d{7,9}$/).optional().nullable()
}).refine((d) => d.email || d.phone, {
  message: "At least email or phone is required"
});
var updateCustomerSchema = createCustomerSchema.partial();
var customerQuerySchema = paginationSchema.extend({
  search: z.string().max(200).optional(),
  tags: z.string().max(500).optional(),
  // comma-separated tag filter
  sortBy: z.enum(["firstName", "totalOrderCount", "totalSpent", "createdAt"]).optional()
});
var customerAddressSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  company: z.string().max(200).optional().nullable(),
  address1: z.string().min(1).max(500),
  address2: z.string().max(500).optional().nullable(),
  city: z.string().min(1).max(200),
  state: z.string().max(200).optional().nullable(),
  postalCode: z.string().max(20).optional().nullable(),
  country: z.string().length(2).default("MM"),
  phone: z.string().optional().nullable(),
  isDefault: z.boolean().default(false)
});
var addCustomerNoteSchema = z.object({
  content: z.string().min(1, "Note cannot be empty").max(2e3)
});
var locationIdSchema = z.string().min(1, { message: "Location is required" });
var inventoryAdjustmentSchema = z.object({
  variantId: idSchema,
  locationId: locationIdSchema,
  /** Positive = add stock, negative = remove stock. Cannot be zero. */
  quantity: z.number().int({ message: "Quantity must be an integer" }).refine((n) => n !== 0, "Quantity cannot be zero"),
  reason: z.enum(["RESTOCK", "ADJUSTMENT", "DAMAGE", "RETURN", "INITIAL"]),
  notes: z.string().max(1e3).optional().nullable()
});
var inventoryTransferSchema = z.object({
  fromLocationId: locationIdSchema,
  toLocationId: locationIdSchema,
  items: z.array(
    z.object({
      variantId: idSchema,
      requestedQty: z.number().int().positive("Quantity must be positive")
    })
  ).min(1, "At least one item is required"),
  notes: z.string().max(1e3).optional().nullable(),
  expectedAt: z.string().datetime({ offset: true }).optional().nullable()
});
var updateTransferStatusSchema = z.object({
  status: z.enum(["PENDING", "IN_TRANSIT", "COMPLETED", "CANCELLED"]),
  notes: z.string().max(1e3).optional().nullable()
});
var inventoryQuerySchema = paginationSchema.extend({
  locationId: locationIdSchema.optional(),
  search: z.string().max(200).optional(),
  sortBy: z.enum(["productName", "sku", "quantityOnHand", "quantityAvailable"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var movementQuerySchema = paginationSchema.extend({
  variantId: idSchema.optional(),
  locationId: locationIdSchema.optional(),
  reason: z.enum([
    "INITIAL",
    "SALE",
    "RETURN",
    "RESTOCK",
    "ADJUSTMENT",
    "TRANSFER_IN",
    "TRANSFER_OUT",
    "DAMAGE",
    "VOID"
  ]).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  search: z.string().max(100).optional()
});
var createDiscountSchema = z.object({
  title: z.string().min(1, "Title is required").max(255),
  description: z.string().max(1e3).optional().nullable(),
  // null = automatic discount (no code needed); non-null = code-based
  code: z.string().min(3, "Code must be at least 3 characters").max(50).toUpperCase().regex(/^[A-Z0-9_-]+$/, "Code can only contain letters, numbers, underscores, and hyphens").optional().nullable(),
  type: z.enum(["PERCENTAGE", "FIXED_AMOUNT", "FREE_SHIPPING", "BUY_X_GET_Y"]),
  value: z.number().nonnegative("Value must be non-negative").default(0),
  target: z.enum(["ALL_PRODUCTS", "SPECIFIC_PRODUCTS", "SPECIFIC_CATEGORIES"]).default("ALL_PRODUCTS"),
  eligibleProductIds: z.array(idSchema).default([]),
  eligibleCategoryIds: z.array(idSchema).default([]),
  // BUY_X_GET_Y specific
  buyQuantity: z.number().int().positive().optional().nullable(),
  getQuantity: z.number().int().positive().optional().nullable(),
  getProductIds: z.array(idSchema).default([]),
  // Stacking
  stackable: z.boolean().default(false),
  // Customer eligibility (empty = all customers)
  eligibleCustomerTags: z.array(z.string().max(100)).default([]),
  // Conditions
  minOrderAmount: z.number().int().nonnegative().optional().nullable(),
  minQuantity: z.number().int().positive().optional().nullable(),
  // Usage limits
  usageLimit: z.number().int().positive().optional().nullable(),
  perCustomerLimit: z.number().int().positive().optional().nullable(),
  // Validity window
  startsAt: z.string().datetime({ offset: true }).optional().nullable(),
  endsAt: z.string().datetime({ offset: true }).optional().nullable(),
  isActive: z.boolean().default(true)
});
var updateDiscountSchema = createDiscountSchema.partial();
var discountQuerySchema = paginationSchema.extend({
  search: z.string().optional(),
  type: z.enum(["PERCENTAGE", "FIXED_AMOUNT", "FREE_SHIPPING", "BUY_X_GET_Y"]).optional(),
  status: z.enum(["active", "scheduled", "expired", "disabled"]).optional(),
  sortBy: z.enum(["title", "usageCount", "startsAt", "endsAt", "createdAt"]).default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var validateDiscountSchema = z.object({
  code: z.string().min(1, "Discount code is required"),
  storeId: idSchema.optional(),
  // required when called without auth (storefront/POS)
  cartSubtotal: z.number().int().nonnegative(),
  lineItems: z.array(
    z.object({
      productId: idSchema,
      categoryIds: z.array(idSchema).default([]),
      quantity: z.number().int().positive(),
      price: z.number().int().nonnegative()
    })
  ),
  customerId: idSchema.optional().nullable()
});
var createCategorySchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2e3).optional().nullable(),
  parentId: idSchema.optional().nullable()
});
var posTransactionSchema = z.object({
  lineItems: z.array(orderLineItemSchema).min(1),
  paymentMethod: z.enum(["CASH", "CARD", "MOBILE_BANKING", "QR_CODE", "CREDIT"]),
  amountTendered: z.number().nonnegative().optional(),
  // for cash payments
  customerId: idSchema.optional().nullable(),
  couponCode: z.string().optional(),
  notes: z.string().max(500).optional()
});
var openSessionSchema = z.object({
  registerId: idSchema,
  openingFloat: z.number().int().nonnegative("Opening float must be a non-negative integer"),
  notes: z.string().max(1e3).optional().nullable()
});
var closeSessionSchema = z.object({
  closingFloat: z.number().int().nonnegative("Closing float must be a non-negative integer"),
  notes: z.string().max(1e3).optional().nullable()
});
var cashMovementSchema = z.object({
  type: z.enum(["CASH_IN", "CASH_OUT"]),
  amount: z.number().int().positive("Amount must be positive"),
  reason: z.string().min(1, "Reason is required").max(500),
  notes: z.string().max(1e3).optional().nullable()
});
var createUserSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100).trim(),
  lastName: z.string().min(1, "Last name is required").max(100).trim(),
  email: z.string().email("Invalid email address").toLowerCase().trim(),
  password: passwordSchema,
  phone: z.string().regex(/^(\+?95|0)9\d{7,9}$/, "Invalid Myanmar phone number").optional().nullable(),
  role: z.enum(["CASHIER", "STAFF", "ADMIN", "SUPER_ADMIN"]),
  customRoleId: z.string().uuid().optional().nullable(),
  storeId: z.string().uuid().optional().nullable()
});
var updateUserSchema = z.object({
  firstName: z.string().min(1).max(100).trim().optional(),
  lastName: z.string().min(1).max(100).trim().optional(),
  email: z.string().email().toLowerCase().trim().optional(),
  phone: z.string().regex(/^(\+?95|0)9\d{7,9}$/).optional().nullable(),
  role: z.enum(["CASHIER", "STAFF", "ADMIN", "SUPER_ADMIN"]).optional(),
  customRoleId: z.string().uuid().optional().nullable(),
  isActive: z.boolean().optional(),
  storeId: z.string().uuid().optional().nullable()
});
var adminResetPasswordSchema = z.object({
  newPassword: passwordSchema
});
var userQuerySchema = paginationSchema.extend({
  search: z.string().max(200).optional(),
  role: z.enum(["CASHIER", "STAFF", "ADMIN", "SUPER_ADMIN"]).optional(),
  isActive: z.coerce.boolean().optional(),
  sortBy: z.enum(["firstName", "email", "role", "createdAt", "lastLoginAt"]).optional()
});
var createCustomRoleSchema = z.object({
  name: z.string().min(1, "Role name is required").max(100).trim(),
  description: z.string().max(500).optional().nullable(),
  permissions: z.array(z.string()).min(1, "Select at least one permission"),
  storeId: z.string().uuid().optional()
});
var updateCustomRoleSchema = createCustomRoleSchema.partial();
var customRoleQuerySchema = paginationSchema.extend({
  search: z.string().max(200).optional()
});
var updateStoreSettingsSchema = z.object({
  name: z.string().min(1, "Store name is required").max(200).trim(),
  description: z.string().max(2e3).optional().nullable(),
  email: z.string().email("Invalid email address").toLowerCase().trim().optional().nullable(),
  phone: z.string().regex(/^(\+?95|0)9\d{7,9}$/, "Invalid Myanmar phone number").optional().nullable(),
  website: z.string().url("Invalid URL").optional().nullable(),
  address: z.string().max(500).optional().nullable(),
  city: z.string().max(200).optional().nullable(),
  state: z.string().max(200).optional().nullable(),
  postalCode: z.string().max(20).optional().nullable(),
  country: z.string().length(2).default("MM"),
  currency: z.string().min(3).max(3).toUpperCase().default("MMK"),
  timezone: z.string().min(1).max(100).default("Asia/Rangoon"),
  taxRate: z.number().min(0).max(100).default(0),
  taxIncluded: z.boolean().default(false)
});
var createLocationSchema = z.object({
  name: z.string().min(1, "Location name is required").max(200).trim(),
  type: z.enum(["WAREHOUSE", "STORE", "VIRTUAL"]),
  address: z.string().max(500).optional().nullable(),
  city: z.string().max(200).optional().nullable(),
  isActive: z.boolean().default(true)
});
var updateLocationSchema = createLocationSchema.partial();
var updatePaymentSettingsSchema = z.object({
  enabledMethods: z.array(z.enum(["CASH", "CARD", "MOBILE_BANKING", "QR_CODE", "CREDIT", "GIFT_CARD"])).default(["CASH", "CARD"]),
  gateway: z.object({
    provider: z.enum(["manual", "stripe", "paypal"]).default("manual"),
    publishableKey: z.string().max(500).default(""),
    secretKey: z.string().max(500).default("")
  }).optional(),
  posPaymentMethods: z.array(
    z.object({
      id: z.string().min(1).max(100),
      name: z.string().trim().min(1, "Method name is required").max(50),
      icon: z.string().max(16).default(""),
      type: z.enum(["CASH", "CARD", "MOBILE_BANKING", "QR_CODE"])
    })
  ).min(1, "Keep at least one POS payment method").optional(),
  mobileBanking: z.object({
    instructions: z.string().max(1e3).default(""),
    accounts: z.array(
      z.object({
        id: z.string(),
        provider: z.string().max(100),
        accountName: z.string().max(200),
        accountNumber: z.string().max(200),
        qrCodeUrl: z.string().nullable()
      })
    ).default([])
  }).optional()
});
var updateStaffSchema = z.object({
  role: z.enum(["CASHIER", "STAFF", "ADMIN"]).optional(),
  isActive: z.boolean().optional(),
  locationIds: z.array(z.string()).optional()
});
var updateCheckoutSettingsSchema = z.object({
  guestCheckout: z.boolean().default(true),
  requiredFields: z.array(z.enum(["email", "phone", "address"])).default(["email"]),
  orderNumberPrefix: z.string().trim().max(20).default("ORD")
});
var updateNotificationSettingsSchema = z.object({
  email: z.object({
    orderConfirmation: z.boolean().default(true),
    shippingUpdate: z.boolean().default(true),
    deliveryConfirmation: z.boolean().default(true),
    refundProcessed: z.boolean().default(true)
  }).optional(),
  admin: z.object({
    newOrder: z.boolean().default(true),
    lowStock: z.boolean().default(false),
    newCustomer: z.boolean().default(false),
    dailySummary: z.boolean().default(false)
  }).optional()
});
var updatePosSettingsSchema = z.object({
  receiptHeader: z.string().trim().max(500).default(""),
  receiptFooter: z.string().trim().max(500).default(""),
  autoPrintReceipt: z.boolean().default(false),
  cashRounding: z.number().int().min(0).max(1e3).default(0),
  barcodeFormat: z.enum(["CODE128", "CODE39", "EAN13", "QR"]).default("CODE128")
});
var posReturnSchema = z.object({
  orderId: idSchema,
  posSessionId: idSchema,
  returnItems: z.array(
    z.object({
      orderLineItemId: idSchema,
      variantId: idSchema.nullable().optional(),
      quantity: z.number().int().min(1),
      restockType: z.enum(["RESTOCK", "NO_RESTOCK"]).default("RESTOCK"),
      locationId: idSchema.optional().nullable()
    })
  ).min(1, "At least one item is required for return"),
  reason: z.enum(["WRONG_ITEM", "DEFECTIVE", "CHANGED_MIND", "OTHER"]),
  notes: z.string().max(1e3).optional().nullable(),
  refundMethod: z.enum(["ORIGINAL_PAYMENT", "STORE_CREDIT", "EXCHANGE"]),
  exchangeItems: z.array(
    z.object({
      variantId: idSchema,
      quantity: z.number().int().min(1)
    })
  ).optional()
});
var offlineTransactionSchema = z.object({
  id: z.string().uuid(),
  sessionId: idSchema,
  lineItems: z.array(orderLineItemSchema).min(1),
  payments: z.array(
    z.object({
      method: z.enum(["CASH", "CARD", "MOBILE_BANKING", "QR_CODE", "CREDIT", "GIFT_CARD"]),
      amount: z.number().int().positive(),
      tendered: z.number().nonnegative().optional(),
      label: z.string().max(50).optional()
    })
  ).min(1),
  customerId: idSchema.optional().nullable(),
  discountCode: z.string().optional().nullable(),
  locationId: idSchema.optional().nullable(),
  notes: z.string().max(2e3).optional().nullable(),
  createdAt: z.number().int().positive()
});
var posSyncSchema = z.object({
  transactions: z.array(offlineTransactionSchema).min(1).max(100)
});
var supplierContactSchema = z.object({
  name: z.string().min(1, "Contact name is required").max(200).trim(),
  role: z.string().max(100).optional().nullable(),
  email: z.string().email("Invalid email").optional().nullable(),
  phone: z.string().max(30).optional().nullable(),
  isPrimary: z.boolean().default(false)
});
var createSupplierSchema = z.object({
  code: z.string().min(1, "Supplier code is required").max(50).trim().toUpperCase(),
  name: z.string().min(1, "Supplier name is required").max(200).trim(),
  email: z.string().email("Invalid email address").optional().nullable(),
  phone: z.string().max(30).optional().nullable(),
  website: z.string().url("Invalid URL").optional().nullable(),
  address: z.string().max(500).optional().nullable(),
  city: z.string().max(200).optional().nullable(),
  country: z.string().length(2).default("MM"),
  paymentTerms: z.coerce.number().int().min(0).max(365).optional().nullable(),
  taxId: z.string().max(100).optional().nullable(),
  notes: z.string().max(2e3).optional().nullable(),
  contacts: z.array(supplierContactSchema).default([])
});
var updateSupplierSchema = createSupplierSchema.partial().extend({
  status: z.enum(["ACTIVE", "INACTIVE", "BLACKLISTED"]).optional()
});
var supplierQuerySchema = paginationSchema.extend({
  search: z.string().max(200).optional(),
  status: z.enum(["ACTIVE", "INACTIVE", "BLACKLISTED"]).optional(),
  sortBy: z.enum(["name", "code", "createdAt"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var createSupplierContactSchema = supplierContactSchema;
var updateSupplierContactSchema = supplierContactSchema.partial();
var createSupplierProductSchema = z.object({
  variantId: idSchema,
  supplierSku: z.string().max(100).optional().nullable(),
  costPrice: z.number().int().nonnegative("Cost price must be non-negative"),
  leadTimeDays: z.coerce.number().int().min(0).optional().nullable(),
  moq: z.coerce.number().int().min(1).default(1),
  isPreferred: z.boolean().default(false)
});
var updateSupplierProductSchema = createSupplierProductSchema.partial().omit({ variantId: true });
var purchaseOrderItemSchema = z.object({
  variantId: idSchema,
  productName: z.string().min(1).max(300),
  variantTitle: z.string().min(1).max(300),
  sku: z.string().max(100).optional().nullable(),
  orderedQty: z.number().int().min(1, "Quantity must be at least 1"),
  costPrice: z.number().int().nonnegative("Cost price must be non-negative")
});
var createPurchaseOrderSchema = z.object({
  supplierId: idSchema,
  locationId: idSchema,
  expectedAt: z.string().datetime().optional().nullable(),
  notes: z.string().max(2e3).optional().nullable(),
  referenceNumber: z.string().max(200).optional().nullable(),
  taxAmount: z.number().int().nonnegative().default(0),
  shippingCost: z.number().int().nonnegative().default(0),
  items: z.array(purchaseOrderItemSchema).min(1, "At least one item is required")
});
var updatePurchaseOrderSchema = createPurchaseOrderSchema.partial().omit({ items: true }).extend({
  items: z.array(purchaseOrderItemSchema).min(1).optional()
});
var purchaseOrderQuerySchema = paginationSchema.extend({
  search: z.string().max(200).optional(),
  supplierId: idSchema.optional(),
  status: z.enum(["DRAFT", "SENT", "PARTIAL", "RECEIVED", "CANCELLED"]).optional(),
  locationId: idSchema.optional(),
  sortBy: z.enum(["poNumber", "totalAmount", "createdAt", "expectedAt"]).default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var receivePurchaseOrderSchema = z.object({
  items: z.array(
    z.object({
      itemId: idSchema,
      receivedQty: z.number().int().nonnegative()
    })
  ).min(1),
  referenceNumber: z.string().max(200).optional().nullable(),
  notes: z.string().max(2e3).optional().nullable()
});
var contactSubmissionSchema = z.object({
  name: z.string().min(1).max(100),
  email: z.string().email(),
  phone: z.string().max(30).optional(),
  subject: z.enum(["order", "return", "product", "seller", "other"]).optional(),
  message: z.string().min(10).max(2e3)
});
var updateContactSubmissionSchema = z.object({
  status: z.enum(["UNREAD", "READ", "REPLIED", "ARCHIVED"]).optional(),
  notes: z.string().max(2e3).nullable().optional()
});
var contactQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  status: z.enum(["UNREAD", "READ", "REPLIED", "ARCHIVED"]).optional(),
  q: z.string().max(200).optional()
});
var createExpenseSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD"),
  amount: z.number().int().positive("Amount must be a positive integer"),
  category: z.string().min(1, "Category is required").max(100),
  description: z.string().min(1, "Description is required").max(255),
  notes: z.string().max(1e3).optional().nullable(),
  locationId: z.string().optional().nullable()
});
var updateExpenseSchema = createExpenseSchema.partial();
var expenseQuerySchema = z.object({
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  category: z.string().optional(),
  locationId: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(50)
});
var updateAnnouncementSettingsSchema = z.object({
  isEnabled: z.boolean(),
  text: z.string().min(1).max(500).trim(),
  linkLabel: z.string().max(50).trim().optional(),
  linkUrl: z.string().max(500).trim().optional()
});

// src/permissions/index.ts
var PERMISSIONS = {
  // ── Dashboard ──
  DASHBOARD_VIEW: "dashboard:view",
  // ── Products ──
  PRODUCTS_VIEW: "products:view",
  PRODUCTS_CREATE: "products:create",
  PRODUCTS_UPDATE: "products:update",
  PRODUCTS_DELETE: "products:delete",
  // ── Orders ──
  ORDERS_VIEW: "orders:view",
  ORDERS_CREATE: "orders:create",
  // place order / POS sale
  ORDERS_UPDATE: "orders:update",
  // status changes, fulfillment
  ORDERS_REFUND: "orders:refund",
  // issue refunds
  // ── Inventory ──
  INVENTORY_VIEW: "inventory:view",
  INVENTORY_ADJUST: "inventory:adjust",
  // manual stocktake corrections
  INVENTORY_TRANSFER: "inventory:transfer",
  // transfer between locations
  // ── Customers ──
  CUSTOMERS_VIEW: "customers:view",
  CUSTOMERS_CREATE: "customers:create",
  CUSTOMERS_UPDATE: "customers:update",
  CUSTOMERS_DELETE: "customers:delete",
  // ── Discounts ──
  DISCOUNTS_VIEW: "discounts:view",
  DISCOUNTS_MANAGE: "discounts:manage",
  // create / update / delete
  // ── Users / Staff ──
  USERS_VIEW: "users:view",
  USERS_MANAGE: "users:manage",
  // invite, change roles, deactivate
  // ── POS ──
  POS_ACCESS: "pos:access",
  // open POS app
  POS_SESSION_MANAGE: "pos:session_manage",
  // open / close register session
  POS_CASH_MANAGE: "pos:cash_manage",
  // cash in / cash out
  // ── Reports ──
  REPORTS_VIEW: "reports:view",
  REPORTS_EXPORT: "reports:export",
  // ── Settings ──
  SETTINGS_VIEW: "settings:view",
  SETTINGS_MANAGE: "settings:manage"
  // store config, taxes, payment methods
};
var CASHIER_PERMISSIONS = [
  PERMISSIONS.POS_ACCESS,
  PERMISSIONS.PRODUCTS_VIEW,
  PERMISSIONS.INVENTORY_VIEW,
  PERMISSIONS.ORDERS_VIEW,
  PERMISSIONS.ORDERS_CREATE,
  PERMISSIONS.CUSTOMERS_VIEW,
  PERMISSIONS.CUSTOMERS_CREATE
];
var STAFF_PERMISSIONS = [
  ...CASHIER_PERMISSIONS,
  PERMISSIONS.DASHBOARD_VIEW,
  PERMISSIONS.POS_SESSION_MANAGE,
  PERMISSIONS.POS_CASH_MANAGE,
  PERMISSIONS.ORDERS_UPDATE,
  PERMISSIONS.CUSTOMERS_UPDATE,
  PERMISSIONS.INVENTORY_ADJUST,
  PERMISSIONS.DISCOUNTS_VIEW,
  PERMISSIONS.PRODUCTS_CREATE,
  PERMISSIONS.PRODUCTS_UPDATE
];
var ADMIN_PERMISSIONS = [
  ...STAFF_PERMISSIONS,
  PERMISSIONS.PRODUCTS_CREATE,
  PERMISSIONS.PRODUCTS_UPDATE,
  PERMISSIONS.PRODUCTS_DELETE,
  PERMISSIONS.ORDERS_REFUND,
  PERMISSIONS.CUSTOMERS_DELETE,
  PERMISSIONS.INVENTORY_TRANSFER,
  PERMISSIONS.DISCOUNTS_MANAGE,
  PERMISSIONS.USERS_VIEW,
  PERMISSIONS.USERS_MANAGE,
  PERMISSIONS.REPORTS_VIEW,
  PERMISSIONS.REPORTS_EXPORT,
  PERMISSIONS.SETTINGS_VIEW,
  PERMISSIONS.SETTINGS_MANAGE
];
var ALL_PERMISSIONS = Object.values(PERMISSIONS);
var ROLE_PERMISSIONS = {
  CUSTOMER: [],
  // no admin/POS permissions
  CASHIER: CASHIER_PERMISSIONS,
  STAFF: STAFF_PERMISSIONS,
  ADMIN: ADMIN_PERMISSIONS,
  SUPER_ADMIN: ALL_PERMISSIONS
  // unrestricted
};
function hasPermission(role, ...permissions) {
  const granted = ROLE_PERMISSIONS[role];
  return permissions.every((p) => granted.includes(p));
}
function hasAnyPermission(role, permissions) {
  const granted = ROLE_PERMISSIONS[role];
  return permissions.some((p) => granted.includes(p));
}
function getPermissionsForRole(role) {
  return ROLE_PERMISSIONS[role];
}
export {
  ADMIN_ROLES,
  CACHE_TTL,
  CURRENCY,
  ERROR_CODES,
  HTTP_STATUS,
  ORDER_CHANNEL,
  ORDER_STATUS,
  PAGINATION,
  PAYMENT_METHOD,
  PAYMENT_STATUS,
  PERMISSIONS,
  POS_ROLES,
  PRODUCT_STATUS,
  ROLE_PERMISSIONS,
  STAFF_ROLES,
  TERMINAL_ORDER_STATUSES,
  UPLOAD,
  USER_ROLES,
  WS_EVENTS,
  addCustomerNoteSchema,
  adminResetPasswordSchema,
  applyDiscount,
  buildPaginationMeta,
  cashMovementSchema,
  changePasswordSchema,
  clamp,
  closeSessionSchema,
  compact,
  contactQuerySchema,
  contactSubmissionSchema,
  createCategorySchema,
  createCustomRoleSchema,
  createCustomerSchema,
  createDiscountSchema,
  createExpenseSchema,
  createLocationSchema,
  createOrderSchema,
  createProductSchema,
  createPurchaseOrderSchema,
  createRefundSchema,
  createSupplierContactSchema,
  createSupplierProductSchema,
  createSupplierSchema,
  createUserSchema,
  customRoleQuerySchema,
  customerAddressSchema,
  customerQuerySchema,
  discountQuerySchema,
  expenseQuerySchema,
  forgotPasswordSchema,
  formatCurrency,
  formatDate,
  formatDateTime,
  generateOrderNumber,
  getPermissionsForRole,
  hasAnyPermission,
  hasPermission,
  idSchema,
  imageReorderSchema,
  inventoryAdjustmentSchema,
  inventoryQuerySchema,
  inventoryTransferSchema,
  inviteStaffSchema,
  isValidEmail,
  isValidPhone,
  loginSchema,
  movementQuerySchema,
  normalizePagination,
  offlineTransactionSchema,
  omit,
  openSessionSchema,
  orderAddressSchema,
  orderLineItemSchema,
  orderQuerySchema,
  paginationSchema,
  paginationToSkipTake,
  pick,
  posReturnSchema,
  posSyncSchema,
  posTransactionSchema,
  productQuerySchema,
  productVariantSchema,
  purchaseOrderItemSchema,
  purchaseOrderQuerySchema,
  quickAddCustomerSchema,
  receivePurchaseOrderSchema,
  refreshTokenSchema,
  refundAmountFor,
  registerSchema,
  resetPasswordSchema,
  slugify,
  supplierQuerySchema,
  toStorageAmount,
  truncate,
  updateAnnouncementSettingsSchema,
  updateCheckoutSettingsSchema,
  updateContactSubmissionSchema,
  updateCustomRoleSchema,
  updateCustomerSchema,
  updateDiscountSchema,
  updateExpenseSchema,
  updateLocationSchema,
  updateNotificationSettingsSchema,
  updateOrderStatusSchema,
  updatePaymentSettingsSchema,
  updatePosSettingsSchema,
  updateProductSchema,
  updateProfileSchema,
  updatePurchaseOrderSchema,
  updateStaffSchema,
  updateStoreSettingsSchema,
  updateSupplierContactSchema,
  updateSupplierProductSchema,
  updateSupplierSchema,
  updateTransferStatusSchema,
  updateUserSchema,
  userQuerySchema,
  validateDiscountSchema
};
//# sourceMappingURL=index.mjs.map